import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from "react"
import {Box, IconButton, ListItemIcon, ListItemText, MenuItem, MenuList, Popover, Typography} from "@mui/material"
import PropTypes from "prop-types";
import maplibregl from "maplibre-gl"
import "maplibre-gl/dist/maplibre-gl.css"
import UseMapData from "../../../../data/map";
import axios from "axios";
import {Map as MapIcon, Check as CheckIcon, Layers as LayersIcon} from "@mui/icons-material";
import {MapLayer, MapLayerTitle, MapSelectableLayers, MapStyle} from "../../../../data/map/state";
import MapRoadLayer from "../../../../yokohama/map/layers/road";
import MapSurveyPointLayer from "../../../../yokohama/map/layers/surveyPoint";
import MaplibreManager from "../../../manager/maplibre";
import MapCircleIcon from "../../../../resources/img/map_green_circle_icon.png"
import MapTriangleIcon from "../../../../resources/img/map_red_triangle_icon.png"
import MapTreeDmLayer from "../../../../yokohama/map/layers/treeDm";
import MapTreeLayer from "../../../../yokohama/map/layers/tree";
import MapTreePopupLayer from "../../../layers/popup";
import {AppDataContext, YokohamaDataContext} from "../../../../App";
import YokohamaDataState from "../../../../data/yokohama/state";

export const MapDataContext = React.createContext()

const styles = {
    map: {
        border: '1px solid #bbb',
    },
    layerButton: {
        position: 'absolute',
        top: '4.3rem',
        right: '1rem',
        background: 'white',
        boxShadow: '#666 1px 1px 6px',
        borderRadius: '8px'
    },
    basemapButton: {
        position: 'absolute',
        top: '1rem',
        right: '1rem',
        background: 'white',
        boxShadow: '#666 1px 1px 6px',
        borderRadius: '8px'
    },
}

const MainMapView = ({width, height}) => {

    const useMapData = UseMapData()
    const { state: appState, setTreeInfoModel, setSelectedTreeModel } = useContext(AppDataContext)
    const { state, setStyle: setMapStyle, setLayers: setMapLayers } = useMapData
    const { state: yokohamaState } = useContext(YokohamaDataContext)
    const { addImageIcon } = MaplibreManager()

    const mapRef = useRef()
    const layerButtonRef = useRef()
    const basemapButtonRef = useRef()
    const [style, setStyle] = useState()
    const [map, setMap] = useState()
    const [initLayers, setInitLayers] = useState([])
    const [openLayerMenu, setOpenLayerMenu] = useState(false)
    const [openBasemapMenu, setOpenBasemapMenu] = useState(false)
    const [showTreeLayer, setShowTreeLayer] = useState(true)

    const treeFilter = useMemo(() => {
        return []
    }, [state.conditions, state.treeInfoModel])

    let tm
    useEffect(() => {
        if (map || !style || !width || !height) { return }

        tm = setTimeout(() => {
            initMap()
        }, 100)

        return () => {
            clearTimeout(tm)
        }
    }, [width, height, style])

    useEffect(() => {
        axios.get(`/resources/map_style/${state.style.key}.json5`)
            .then(res => {
                setStyle(res.data)
            })
            .catch(e => {
                console.log('[Map]', 'load style error', e)
            })
    }, [state.style])

    const initMap = useCallback(() => {
        if (map || !style || !width || !height) { return }
        console.log("[Map]", "initialize", style, width, height)
        const m = new maplibregl.Map({
            container: mapRef.current,
            center: [139.63436717182637, 35.450256493720865],
            zoom: 12,
            style,
        })

        Promise.all([
            addImageIcon(m, MapCircleIcon, "circle-icon"),
            addImageIcon(m, MapTriangleIcon, "triangle-icon")
        ]).then(() => {
            console.log("[Map]", "map initialized", m)
        }).catch(e => {
            console.log("[Map]", "add icon error", e)
        })
        setMap(m)
    }, [style, width, height])

    useEffect(() => {
        if (map) {
            map.setStyle(style)
            setInitLayers([])
        }
    }, [style])

    const onChangeMapStyle = (value) => {
        setMapStyle(value)
        setOpenBasemapMenu(false)
    }

    const onChangeMapLayer = useCallback((value) => {
        if (state.layers?.includes(value)) {
            setMapLayers([])
        } else {
            setMapLayers([value])
        }
    }, [state.layers])

    useEffect(() => {
        if (!map || !yokohamaState.selectedTree) { return }
        console.log(yokohamaState)
        map.flyTo({
            center: [yokohamaState.selectedTree.longitude, yokohamaState.selectedTree.latitude],
            zoom: 20,
        })

    }, [map, yokohamaState.selectedTree])

    // useEffect(() => {
    //     if (!map || !appState.selectedTreeModel) { return }
    //
    //     map.flyTo({
    //         center: {
    //             lng: appState.selectedTreeModel.longitude,
    //             lat: appState.selectedTreeModel.latitude,
    //         },
    //         zoom: 18,
    //         speed: 0.8,
    //     })
    //
    // }, [appState.selectedTreeModel])

    return (
        <MapDataContext.Provider value={useMapData}>
            <Box style={{...styles.map, width, height}} ref={mapRef}>
                <MapRoadLayer
                    map={map}
                    visible={state.layers?.includes(MapLayer.Road)}
                    style={state.style?.key}
                    onInit={() => setInitLayers(prev => [...prev, MapLayer.Road])}
                />
                <MapSurveyPointLayer
                    map={map}
                    visible={state.layers?.includes(MapLayer.SurveyPoint)}
                    style={state.style?.key}
                    onInit={() => setInitLayers(prev => [...prev, MapLayer.SurveyPoint])}
                />
                <MapTreeDmLayer
                    map={map}
                    visible={state.layers?.includes(MapLayer.TreeDm)}
                    style={state.style?.key}
                    onInit={() => setInitLayers(prev => [...prev, MapLayer.TreeDm])}
                />
                <MapTreeLayer
                    map={map}
                    visible={true}
                    style={state.style?.key}
                    onInit={() => setInitLayers(prev => [...prev, MapLayer.Tree])}
                    onClick={e => setSelectedTreeModel(e)}
                />
                <MapTreePopupLayer
                    map={map}
                    treeModel={appState.selectedTreeModel}
                    onInfo={setTreeInfoModel}
                    onClose={() => setSelectedTreeModel(null)}
                />

                <IconButton style={{...styles.basemapButton, opacity: openBasemapMenu ? 0: 1}} ref={basemapButtonRef} onClick={() => setOpenBasemapMenu(true)}>
                    <MapIcon />
                </IconButton>
                <IconButton style={{...styles.layerButton, opacity: openLayerMenu ? 0: 1}} ref={layerButtonRef} onClick={() => setOpenLayerMenu(true)}>
                    <LayersIcon />
                </IconButton>
            </Box>
            <Popover
                open={openBasemapMenu}
                anchorEl={basemapButtonRef.current}
                anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "right"
                }}
                onClose={() => setOpenBasemapMenu(false)}
                >
                <MenuList>
                    {Object.entries(MapStyle).map(([key, value]) => {
                        return (
                            <MenuItem key={`map-basemap-menu-${key}`}>
                                <ListItemText style={{width: "140px"}} onClick={() => onChangeMapStyle(value)}>{value.title}</ListItemText>
                                {state.style?.key === value.key && <ListItemIcon><CheckIcon /></ListItemIcon>}
                            </MenuItem>
                        )
                    })}
                </MenuList>
            </Popover>
            <Popover
                open={openLayerMenu}
                anchorEl={layerButtonRef.current}
                anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
                onClose={() => setOpenLayerMenu(false)}
            >
                <MenuList>
                    {MapSelectableLayers.map(key => {
                        return (
                            <MenuItem key={key} onClick={() => onChangeMapLayer(key)}>
                                <ListItemText style={{width: "140px"}}>{MapLayerTitle[key]}</ListItemText>
                                {state.layers?.includes(key) && (
                                    <ListItemIcon><CheckIcon /></ListItemIcon>
                                )}
                            </MenuItem>
                        )
                    })}
                </MenuList>
            </Popover>
        </MapDataContext.Provider>
    )
}

MainMapView.propTypes = {
    width: PropTypes.any,
    height: PropTypes.any,
}

export default MainMapView
